import { useParams, Link } from 'react-router-dom';
import { useState, useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { comics, categories } from '@/mocks/comics';

const ITEMS_PER_PAGE = 12;

type SortOption = 'update' | 'rating' | 'views' | 'likes';
type StatusFilter = 'all' | '连载中' | '已完结' | '停更';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [sortBy, setSortBy] = useState<SortOption>('update');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [currentPage, setCurrentPage] = useState(1);

  const categoryInfo = categories.find((c) => c.slug === slug);
  const currentCategory = categoryInfo || { name: '全部', slug: 'all' };

  const filteredComics = useMemo(() => {
    let result = slug === 'all'
      ? comics
      : comics.filter((c) => c.categories.includes(slug || ''));

    if (statusFilter !== 'all') {
      result = result.filter((c) => c.status === statusFilter);
    }

    const sorted = [...result];
    switch (sortBy) {
      case 'update':
        sorted.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
        break;
      case 'rating':
        sorted.sort((a, b) => b.rating - a.rating);
        break;
      case 'views':
        sorted.sort((a, b) => b.views - a.views);
        break;
      case 'likes':
        sorted.sort((a, b) => b.likes - a.likes);
        break;
      default:
        break;
    }
    return sorted;
  }, [slug, sortBy, statusFilter]);

  const totalPages = Math.ceil(filteredComics.length / ITEMS_PER_PAGE);
  const paginatedComics = filteredComics.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const sortLabels: Record<SortOption, string> = {
    update: '最新更新',
    rating: '评分最高',
    views: '最多阅读',
    likes: '最多点赞',
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <>
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: `${currentCategory.name} - 韩漫分类 - 韩漫之家`,
          description: `韩漫之家${currentCategory.name}分类，汇聚海量精品韩漫，免费下拉式阅读，智能加载流畅体验。`,
          url: `http://rfeff.com/category/${currentCategory.slug}`,
          isPartOf: {
            '@type': 'WebSite',
            name: '韩漫之家',
            url: 'http://rfeff.com/',
          },
          mainEntity: {
            '@type': 'ItemList',
            itemListElement: filteredComics.slice(0, 20).map((comic, index) => ({
              '@type': 'ListItem',
              position: index + 1,
              item: {
                '@type': 'ComicSeries',
                name: comic.title,
                url: `http://rfeff.com/comic/${comic.slug}`,
                author: { '@type': 'Person', name: comic.author },
                image: comic.coverImage,
              },
            })),
          },
        })}
      </script>

      <Navbar />

      <main className="pt-20 min-h-screen">
        <div className="max-w-[1440px] mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: currentCategory.name },
            ]}
          />

          <div className="py-6 md:py-8">
            <div className="flex items-center gap-3 mb-2">
              {categoryInfo && (
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                  <i className={`${categoryInfo.icon} text-lg`}></i>
                </div>
              )}
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                {slug === 'all' ? '全部分类' : currentCategory.name}
              </h1>
            </div>
            <p className="text-sm text-foreground-500">
              共 {filteredComics.length} 部作品
              {statusFilter !== 'all' && ` · ${statusFilter}`}
              {sortBy !== 'update' && ` · 按${sortLabels[sortBy]}排序`}
            </p>
          </div>

          {/* 分类标签 */}
          <div className="flex flex-wrap gap-2 mb-4">
            <Link
              to="/category/all"
              className={`whitespace-nowrap px-4 py-2 rounded-full text-sm transition-colors cursor-pointer ${
                slug === 'all'
                  ? 'bg-primary-500 text-white'
                  : 'bg-background-100 text-foreground-600 hover:bg-background-200'
              }`}
            >
              全部
            </Link>
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`whitespace-nowrap px-4 py-2 rounded-full text-sm transition-colors cursor-pointer ${
                  slug === cat.slug
                    ? 'bg-primary-500 text-white'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>

          {/* 筛选和排序 */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6 py-4 border-y border-background-200/70">
            {/* 状态筛选 */}
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs text-foreground-400">状态：</span>
              {(['all', '连载中', '已完结', '停更'] as StatusFilter[]).map((s) => (
                <button
                  key={s}
                  onClick={() => {
                    setStatusFilter(s);
                    setCurrentPage(1);
                  }}
                  className={`whitespace-nowrap px-3 py-1.5 rounded-full text-xs transition-colors cursor-pointer ${
                    statusFilter === s
                      ? 'bg-secondary-500 text-white'
                      : 'bg-background-100 text-foreground-500 hover:bg-background-200'
                  }`}
                >
                  {s === 'all' ? '全部' : s}
                </button>
              ))}
            </div>

            {/* 排序 */}
            <div className="flex items-center gap-2">
              <span className="text-xs text-foreground-400">排序：</span>
              <select
                value={sortBy}
                onChange={(e) => {
                  setSortBy(e.target.value as SortOption);
                  setCurrentPage(1);
                }}
                className="px-3 py-1.5 rounded-full text-xs bg-background-100 text-foreground-600 border border-background-200/70 focus:outline-none focus:border-primary-300 cursor-pointer"
              >
                <option value="update">最新更新</option>
                <option value="rating">评分最高</option>
                <option value="views">最多阅读</option>
                <option value="likes">最多点赞</option>
              </select>
            </div>
          </div>

          {/* 漫画网格 */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-5">
            {paginatedComics.map((comic) => (
              <Link
                key={comic.id}
                to={`/comic/${comic.slug}`}
                className="group cursor-pointer"
                data-product-shop=""
              >
                <div className="relative aspect-[3/4] rounded-xl overflow-hidden mb-3 bg-background-200">
                  <img
                    src={comic.coverImage}
                    alt={comic.title}
                    title={`${comic.title} - 韩漫之家`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                  <div className="absolute top-2 left-2">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
                      comic.status === '连载中'
                        ? 'bg-primary-500 text-white'
                        : comic.status === '已完结'
                        ? 'bg-accent-500 text-white'
                        : 'bg-secondary-500 text-white'
                    }`}>
                      {comic.status}
                    </span>
                  </div>
                  <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/60 to-transparent p-2 pt-6 opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="flex items-center gap-2 text-white text-xs">
                      <div className="w-3 h-3 flex items-center justify-center">
                        <i className="ri-star-fill text-accent-400 text-[10px]"></i>
                      </div>
                      <span>{comic.rating}</span>
                      <span>·</span>
                      <span>{(comic.views / 10000).toFixed(0)}万阅读</span>
                    </div>
                  </div>
                </div>
                <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-1">
                  {comic.title}
                </h3>
                <p className="text-xs text-foreground-500 mt-1">{comic.author}</p>
                <div className="flex items-center gap-2 mt-1.5">
                  <div className="flex items-center gap-0.5">
                    <div className="w-3 h-3 flex items-center justify-center">
                      <i className="ri-star-fill text-accent-500 text-[10px]"></i>
                    </div>
                    <span className="text-xs text-foreground-600">{comic.rating}</span>
                  </div>
                  <span className="text-xs text-foreground-400">
                    更新至{comic.chapters.length}话
                  </span>
                </div>
                <div className="flex flex-wrap gap-1 mt-1.5">
                  {comic.tags.slice(0, 2).map((tag) => (
                    <span key={tag} className="text-[10px] px-1.5 py-0.5 rounded bg-background-100 text-foreground-500">
                      {tag}
                    </span>
                  ))}
                </div>
              </Link>
            ))}
          </div>

          {/* 空状态 */}
          {paginatedComics.length === 0 && (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
                <i className="ri-emotion-sad-line text-2xl"></i>
              </div>
              <p className="text-foreground-500 mb-2">该筛选条件下暂无作品</p>
              <button
                onClick={() => {
                  setStatusFilter('all');
                  setSortBy('update');
                  setCurrentPage(1);
                }}
                className="text-sm text-primary-500 hover:text-primary-600 cursor-pointer"
              >
                清除筛选
              </button>
            </div>
          )}

          {/* 分页 */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-10 mb-10">
              <button
                onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="w-9 h-9 flex items-center justify-center rounded-full border border-background-300 text-foreground-500 hover:text-primary-500 hover:border-primary-300 transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <i className="ri-arrow-left-s-line"></i>
              </button>

              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  onClick={() => handlePageChange(page)}
                  className={`w-9 h-9 flex items-center justify-center rounded-full text-sm transition-colors cursor-pointer ${
                    currentPage === page
                      ? 'bg-primary-500 text-white'
                      : 'border border-background-300 text-foreground-600 hover:text-primary-500 hover:border-primary-300'
                  }`}
                >
                  {page}
                </button>
              ))}

              <button
                onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="w-9 h-9 flex items-center justify-center rounded-full border border-background-300 text-foreground-500 hover:text-primary-500 hover:border-primary-300 transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <i className="ri-arrow-right-s-line"></i>
              </button>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </>
  );
}